<template>
  <section class="dashboard-content-area">
    <TopMenu
      :tableSpecifications="tableSpecifications"
      :selectedCountrySpecification="selectedCountrySpecification"
      :groupedFilters="groupedFilters"
      :table="table"
      @toggleIconClicked="toggleSideBar"
      :dataView="dataView"
      @filtersChanged="filtersChanged = !filtersChanged"
    />
    <TableSubMenu
      :tableSpecifications="tableSpecifications"
      :selectedCountrySpecification="selectedCountrySpecification"
      :groupedFilters="groupedFilters"
      :table="table"
      @columnsChanged="this.initialiseTable"
      @countryChanged="this.onTableSpecificationChange"
      @filtersChanged="filtersChanged = !filtersChanged"
    />
    <section
      class="data-area"
      :class="{ loading: dataView[0] === 'loading' || dataView[0] === 'chart' }"
    >
      <div v-show="dataView[0] === 'loading'" class="loader"></div>
      <div v-show="dataView[0] === 'table'" ref="table" id="table"></div>
      <Charts
        v-if="dataView[0] === 'chart'"
        :table="table"
        :selectedCountrySpecification="selectedCountrySpecification"
        :filtersChanged="filtersChanged"
        :tableSpecifications="tableSpecifications"
      />
    </section>
  </section>
  <CardModal
    :isActive="activateModal"
    @closeModal="toggleModal"
    :record="modalRecord"
    :groupedFilters="groupedFilters"
  />
</template>

<script>
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { url } from "../App.vue";
import { DateTime } from "luxon";
import CardModal from "../components/CardModal.vue";
import TopMenu from "./TopMenu.vue";
import Charts from "./Charts.vue";
import TableSubMenu from "./TableSubMenu.vue";

import _ from "lodash";

export default {
  props: ["tableSpecifications"],
  data() {
    return {
      table: null,
      tableData: [],
      groupedFilters: [],
      selectedCountrySpecification: {
        tableDefinition: [],
      },
      dataView: ["loading"],
      activateModal: false,
      modalRecord: [],
      filtersChanged: false,
    };
  },
  methods: {
    toggleSideBar,
    retrieveTableData,
    initialiseTable,
    storeFilters,
    retrieveFilters,
    storeUserColumnCustomisations,
    constructLocalStorageID,
    onTableSpecificationChange,
    viewRecordAttachments,
    toggleModal,
    originalSpecificationChanged,
    clearLocalStorage,
    addTableButtons,
  },
  components: {
    CardModal,
    TopMenu,
    TableSubMenu,
    Charts,
  },
  watch: {
    // whenever tableSpecifications changes, this function will run
    tableSpecifications: onTableSpecificationChange,
    selectedCountrySpecification: {
      async handler(newCountrySpecification, oldCountrySpecification) {
        if (
          oldCountrySpecification !== null ||
          oldCountrySpecification.length != 0
        ) {
          this.dataView[0] = "loading";
          // Ensure that the user's changes persist on a prop change
          this.storeFilters(oldCountrySpecification);
          this.storeUserColumnCustomisations(oldCountrySpecification);
        }
        await this.retrieveTableData(newCountrySpecification);
        this.retrieveFilters(newCountrySpecification);
      },
      // force eager callback execution which would allow the methods invoked to execute using all
      // old component property values when the watched property has been changed.
      immediate: true,
    },
  },
  beforeUnmount() {
    // Ensure that the user's changes persist on change to another component/page such as the summary page
    this.storeUserColumnCustomisations(this.selectedCountrySpecification);
    this.storeFilters(this.selectedCountrySpecification);
  },
  async beforeMount() {
    // Add luxon date library to the Tabulator form via a property to the global object
    window.DateTime = DateTime;
    // Ensure that the user's changes persist on page reload
    window.addEventListener("unload", () => {
      this.storeUserColumnCustomisations(this.selectedCountrySpecification);
      this.storeFilters(this.selectedCountrySpecification);
    });
    // Ensure that default table view loaded on user login is initialised.
    await this.retrieveTableData(this.selectedCountrySpecification);
    this.retrieveFilters(this.selectedCountrySpecification);
  },
};

function onTableSpecificationChange() {
  const originaltableSpecifications = this.tableSpecifications.countries.find(
    (country) => country.active
  );
  // Created a copy of the table specifications so that edits to the object within current page does not affect the server's table definition
  this.selectedCountrySpecification = structuredClone(
    originaltableSpecifications
  );
}

function storeUserColumnCustomisations(specification) {
  if (
    this.table === null ||
    specification == null ||
    this.dataView[0] === "loading"
  )
    return;
  const userCustomisation = this.table.getColumnLayout();

  // Remove toggle additional info field and get attachments value before storing the column layout
  specification.tableDefinition.pop();
  userCustomisation.pop();

  // If the view has attachments associated with the record, remove the attachment button before storing column specification
  // Only remove attachments column if the table records had the attachment column inserted
  if (specification.attachments) {
    specification.tableDefinition.pop();
    userCustomisation.pop();
  }
  const mergedColumnCustomisation = userCustomisation.map((column, index) => {
    return {
      // Merge initial config with user customisations.
      // Prioritise user customisations to overrwrite initial structure
      // However allow the initial structure visibility to persist since it preserves the visibility of fields that are viewed within the collapsed window.
      // The specification.tableDefinition also contains the user's column visibility selection therefore user column visibility will persist.
      // All other config are captured within tabulator column layout object.
      ...specification.tableDefinition[index],
      ...column,
      visible: specification.tableDefinition[index].visible,
    };
  });
  const id = this.constructLocalStorageID("columns", specification);
  localStorage.setItem(id, JSON.stringify(mergedColumnCustomisation));
}

function constructLocalStorageID(type, specification) {
  if (specification == null || specification.length == 0) return;
  if (type === "raw") return `${specification.id}`;
  if (type === "columns") return `tabulator-${specification.id}-columns`;
  if (type === "filters") return `tabulator-${specification.id}-filter`;
  if (type === "initialLayout")
    return `tabulator-${specification.id}-initialLayout`;
  if (type === "sort") return `tabulator-${specification.id}-sort`;
}
function retrieveFilters() {
  if (
    this.table == null ||
    this.selectedCountrySpecification.tableDefinition.length == 0
  )
    return;
  // Clear all applied filters to the table
  this.table.clearFilter();
  // read from local storage (ungrouped)
  const filterID = this.constructLocalStorageID(
    "filters",
    this.selectedCountrySpecification
  );
  let tableFilters = JSON.parse(localStorage.getItem(filterID));

  // if tableFilters is empty or null use table filters from http response
  if (tableFilters == null || tableFilters.length == 0) {
    tableFilters = this.selectedCountrySpecification.initialFilter;
  }

  // assign filters to table (ungrouped)
  // Add all filters whose applied === true (applied) to the table
  for (const filter of tableFilters) {
    if (filter[0].applied && filter[0].filterType === "or") {
      this.table.addFilter([filter]);
    } else if (filter[0].applied) {
      this.table.addFilter(filter);
    }
  }
  // group in order to display (grouped)
  this.groupedFilters = tableFilters;
}
function toggleSideBar() {
  this.$emit("toggleIconClicked");
}

function storeFilters(specification) {
  if (this.dataView[0] === "loading") return;
  // Retrieve filters from table (ungrouped)
  const filtersToBeStored = this.groupedFilters;
  this.groupedFilters = [];
  const filterID = this.constructLocalStorageID("filters", specification);
  // write to local storage (ungrouped)
  localStorage.setItem(filterID, JSON.stringify(filtersToBeStored));
}

async function retrieveTableData(newCountrySpecification) {
  if (
    newCountrySpecification == null ||
    newCountrySpecification.tableDefinition.length == 0
  ) {
    return;
  }
  const tableViewColumnID = this.constructLocalStorageID(
    "columns",
    newCountrySpecification
  );
  // Retrieve stored column data for the selected table view based on the unique ID for that menu item
  const storedColumnLayout = JSON.parse(
    localStorage.getItem(tableViewColumnID)
  );

  // Compare the default column layout with the layout retreived from the server
  // If there is a change, reset the table view using the server specifications.
  const originalSpecificationChangeStatus = this.originalSpecificationChanged(
    newCountrySpecification
  );

  // If their is stored column data and the server json data specification for the table has not changed
  // Use the stored column data
  if (storedColumnLayout != null && !originalSpecificationChangeStatus) {
    this.selectedCountrySpecification.tableDefinition = storedColumnLayout;
  }

  // Append the appropriate table buttons to the table view
  this.addTableButtons(newCountrySpecification);

  // retreive the list of fields to be displayed for the table data from the column specification data
  const filteredRequestedFields = retrieveTableFieldsList(
    newCountrySpecification
  );

  // Send request to the server endpoint
  const response = await fetch(
    `${url}${newCountrySpecification.dataEndpoint}?fields=${encodeURI(
      JSON.stringify(filteredRequestedFields)
    )}`,
    {
      credentials: "include", // Ensure that the credentials cookie is stored and issued with cross origin requests.
      method: "GET", // POST, PUT, DELETE, etc.
    }
  );
  // Retrieve the response body and assign the response json data to the appropriate component property
  // Then initialise the table with the retrieved data
  const responseBody = await response.json();

  if (response.ok) {
    this.tableData = responseBody;
    this.initialiseTable(newCountrySpecification);
    // Stop loading animation
    this.dataView[0] = "table";
  }
}

function retrieveTableFieldsList(newCountrySpecification) {
  // Store fields to be displayed within table to be sent within the HTTP request
  const requestFields = newCountrySpecification.tableDefinition.map(
    (column) => {
      if ("field" in column && "compute" in column) {
        return !column.compute ? column.field : null;
      }
    }
  );
  // remove null values from requested field list
  return requestFields.filter((field) => {
    if (field) return field;
  });
}

function addTableButtons(newCountrySpecification) {
  // Add attachment button if the table records have attachments
  if (newCountrySpecification.attachments) {
    newCountrySpecification.tableDefinition.push({
      title: "Attachments",
      formatter: getRecordAttachmentsFormatter,
      headerSort: false,
      visible: true,
      responsive: 0,
      minWidth: 50,
      cellClick: this.viewRecordAttachments,
    });
  }

  // Add toggle buttons to table columns
  newCountrySpecification.tableDefinition.push({
    title: "Additional Info",
    formatter: customResponsiveCollapseFormatter,
    headerSort: false,
    visible: true,
    responsive: 0,
    minWidth: 50,
  });
}

function originalSpecificationChanged(newCountrySpecification) {
  // This function will be used to detect any changes within the server json table column definition
  // If changes to exist, clear local storage for affected table and read column specification from server response.
  const storedInitialLayoutID = this.constructLocalStorageID(
    "initialLayout",
    newCountrySpecification
  );
  const storedInitialLayout = JSON.parse(
    localStorage.getItem(storedInitialLayoutID)
  );
  if (storedInitialLayout !== null) {
    if (_.isEqual(storedInitialLayout, newCountrySpecification)) {
      return false;
    }
  }
  this.clearLocalStorage();
  const initialLayout = JSON.stringify(newCountrySpecification);
  localStorage.setItem(storedInitialLayoutID, initialLayout);
  return true;
}

function clearLocalStorage() {
  const tableColumnSpecificationID = this.constructLocalStorageID(
    "columns",
    this.selectedCountrySpecification
  );
  const tableFilterID = this.constructLocalStorageID(
    "filters",
    this.selectedCountrySpecification
  );
  const tableSortID = this.constructLocalStorageID(
    "sort",
    this.selectedCountrySpecification
  );
  localStorage.removeItem(tableColumnSpecificationID);
  localStorage.removeItem(tableFilterID);
  localStorage.removeItem(tableSortID);
}

function initialiseTable(newCountrySpecification) {
  //create Tabulator on DOM element with id "example-table"
  this.table = new Tabulator(this.$refs.table, {
    minHeight: "100%", // set height of table (in CSS or here), this enables the Virtual DOM and improves render speed dramatically (can be any valid css height value)
    data: this.tableData, //assign data to table
    layout: "fitDataFill", //resize column width to ensure that all displayed columns fit within the width of the table and the data is being correctly displayed
    responsiveLayout: "collapse", //hide columns that dont fit on the table
    history: true, //allow undo and redo actions on the table
    pagination: "local", //paginate the data
    paginationSize: 4, //allow 7 rows per page of data
    paginationCounter: "rows", //display count of paginated rows in footer
    rowHeight: 70, //set rows to 40px height
    movableColumns: true, //allow column order to be changed
    responsiveLayoutCollapseStartOpen: false,
    resizableColumnFit: true,
    autoResize: true, // prevent auto resizing of table
    layoutColumnsOnNewData: true,
    placeholder: "No Data Available", //display message to user on empty table
    persistence: {
      sort: true, //persist sort
    },
    persistenceID: this.constructLocalStorageID("raw", newCountrySpecification),
    columns: this.selectedCountrySpecification.tableDefinition,
    initialSort: newCountrySpecification.initialSort,
  });

  // Add an cell edited event to the table which will use to trigger the method to update shipment comment
  this.table.on("cellEdited", updateKPIComment);
}

function updateKPIComment(cell) {
  const editedRowData = cell.getRow().getData();
  if ("Shipper" in editedRowData) {
    updateShipmentComment(editedRowData);
  } else if ("invoice_date" in editedRowData) {
    updateInvoiceComment(editedRowData);
  }
}
function updateShipmentComment(editedRowData) {
  fetch(`${url}/Exxon/KPI/shipment/comment`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify(editedRowData),
  });
}

function updateInvoiceComment(editedRowData) {
  fetch(`${url}/Exxon/KPI/invoice/comment`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify(editedRowData),
  });
}

function toggleList(isOpen, toggleButtonContainer, cell) {
  // Retrieve the additional info section for the associated row
  const collapse = cell
    .getRow()
    .getElement()
    .getElementsByClassName("tabulator-responsive-collapse")[0];

  let open = isOpen;
  // If the state is open, ensure to display the button with the class open and display the additional columns for that row
  // else ensure to display the button without the open class and hide the additional columns for that row
  if (open) {
    toggleButtonContainer.classList.add("open");
    if (collapse) {
      collapse.style.display = "";
    }
  } else {
    toggleButtonContainer.classList.remove("open");
    if (collapse) {
      collapse.style.display = "none";
    }
  }
  return open;
}

function customResponsiveCollapseFormatter(cell, formatterParams, onRendered) {
  let open = false; // Initial state of the additional information section
  // Create the element to contain the two buttons which will be swapped according to the state of the additional fields.
  const toggleButtonContainer = document.createElement("div");
  // Specify that the element will contain our toggle buttons by adding the appropriate class used by tabulator
  toggleButtonContainer.classList.add("tabulator-responsive-collapse-toggle");
  // Define the toggle buttons to be contained within the column
  toggleButtonContainer.innerHTML = `
    <button class='tabulator-responsive-collapse-toggle-open'>
      Expand
    </button>
    <button class='tabulator-responsive-collapse-toggle-close'>
      Collapse
    </button>`;

  cell.getElement().classList.add("tabulator-row-handle"); // Adds the default styles to the value of the cell

  // If the table property defines the additional columns to be displayed on page load, change the state accordingly
  if (this.table.options.responsiveLayoutCollapseStartOpen) {
    open = true;
  }

  // add event listener to toggle the additional columns display and to change to the appropriate button
  // Update the open state after the method has executed
  toggleButtonContainer.addEventListener("click", function () {
    open = toggleList(!open, toggleButtonContainer, cell);
  });

  // Initialise the table view based on default value
  open = toggleList(open, toggleButtonContainer, cell);

  // Return the customised element containing the toggle buttons to be displayed within the column
  return toggleButtonContainer;
}

function getRecordAttachmentsFormatter(cell) {
  cell.getElement().classList.add("tabulator-row-handle"); // Adds the default styles to the value of the cell
  return `
  <div class="tabulator-responsive-collapse-toggle">
    <button class="attachment-button">
      View
    </button>
  </div>`;
}

function viewRecordAttachments(event, cell) {
  this.modalRecord = cell.getRow().getData();
  this.toggleModal();
}
function toggleModal() {
  this.activateModal = !this.activateModal;
}
</script>

<style lang="scss">
section.dashboard-content-area {
  display: grid;
  grid-template-rows: 1fr 1fr 10fr;
  height: 100vh;
  .control {
    background-color: rgba(247, 244, 244, 0.342);
  }
  .data-area {
    background-color: rgba(247, 244, 244, 0.342);
    grid-row: 3;
    padding-top: 1rem;
    overflow: scroll;
    &.loading {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    #table {
      color: black;
    }
  }
}

.table-title {
  padding: 0 1rem;
}

.tabulator {
  width: 95%;
  margin: 0 auto;
  background-color: rgba(247, 244, 244, 0.342);

  .tabulator-header {
    margin-bottom: 1rem;
    border-radius: 10px;
    background-color: #106eec;
    color: white;
    .tabulator-col {
      padding: 1rem 0rem;
      border-left: 1px solid white;
      &:nth-child(3) {
        border-left: none;
      }
    }
  }
  .tabulator-footer {
    border: none;
    padding: 0;
    padding-bottom: 2rem;
    margin-top: 0.5rem;
    .tabulator-page {
      background-color: white;
      border: none;
      box-shadow: 1px 0px 10px 0px rgba(204, 197, 197, 0.323);
      color: grey;
      &.active {
        background-color: #3ddc97;
        color: white;
      }
    }
  }
}
.tabulator-row {
  min-height: 5rem;
  border: none;
  &.tabulator-row-even,
  &.tabulator-row-odd {
    background-color: white;
    padding: 0.5rem 0;
    border-radius: 10px;
    margin-bottom: 0.4rem;
  }
  .tabulator-cell {
    border: none;
    text-transform: uppercase;
    height: 2.5rem;
    .tabulator-responsive-collapse-toggle {
      width: 100%;
      background: none;
      button {
        background: #3ddc97;
        width: 6rem;
        height: 2.2rem;
        border-radius: 10px;
        border: none;
        color: white;
        cursor: pointer;
        font-weight: bold;

        &:hover {
          border: 1px solid #3ddc97;
          background: white;
          color: #3ddc97;
        }
        &:active {
          border: none;
          background: none;
          color: black;
        }
      }
    }
  }
  .tabulator-responsive-collapse {
    border: none;
    border-top: 1px solid rgba(204, 197, 197, 0.323);
    padding-left: 0.75rem;
    padding-top: 0.75rem;
    margin-top: 0.75rem;
    transition: all 1s ease;
  }
}

.loader {
  border: 16px solid #f3f3f3;
  border-radius: 50%;
  border-top: 16px solid #106eec;
  width: 120px;
  height: 120px;
  -webkit-animation: spin 2s linear infinite; /* Safari */
  animation: spin 2s linear infinite;
}

/* Safari */
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.attachment-button-container {
  width: 100%;
  background: none;
  button {
    background: #3ddc97;
    width: 6rem;
    height: 2.2rem;
    border-radius: 10px;
    border: none;
    color: white;
    cursor: pointer;

    font-weight: bold;

    &:hover {
      border: 1px solid #3ddc97;
      background: white;
      color: #3ddc97;
    }
    &:active {
      border: none;
      background: none;
      color: black;
    }
  }
}

.tabulator-row .tabulator-responsive-collapse table tr td {
  text-transform: uppercase;
}
</style>